import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getDealDeadlines, type DealDeadline } from '../api/home';
import { useIsMobile } from './Layout';
import { formatDate } from '../utils/dates';

/**
 * L-71 (Daniel 2026-07-04): dashboard widget showing every active deal
 * with all key dates + commission at a glance. Colors past-and-still-
 * open dates red, imminent (≤3 days) amber, neutral otherwise.
 *
 * Works uniformly across buyer / seller / auction transaction types —
 * columns are the same, only the type badge changes.
 *
 * Commission column reads from commission_breakdown_json (the shape
 * already set up in migration 0038):
 *   commission_pct   = total commission from contract (e.g. 6%)
 *   buyer_agent_pct  = share given to buyer's agent when we list
 *   →  our-side %   = commission_pct − buyer_agent_pct
 * Deals that haven't had their commission entered show "—".
 *
 * V-1 (Daniel 2026-08-10): the Commission column only exists for leadership.
 * The deals stay team-wide, deliberately, because coordinating deadlines
 * across the team is the whole job of this table. The money on them does not:
 * the server nulls every commission field for anyone who is not owner,
 * operations or admin and sets commission_visible false, and the column is
 * then absent rather than a stack of dashes. A dash means "not entered yet",
 * which is a different and misleading statement.
 */
export default function DealDeadlinesCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['deal-deadlines'],
    queryFn: getDealDeadlines,
    staleTime: 30_000,
  });

  const deals = data?.deals ?? [];
  // Defaults to hidden while the request is in flight, so the column never
  // appears and then vanishes.
  const showCommission = data?.commission_visible === true;
  const nowSec = useMemo(() => Math.floor(Date.now() / 1000), []);
  // A 10-column, 900px-wide table can't be made to work on a phone. On
  // mobile we render the same deals + the same dates as stacked cards.
  // (Daniel 2026-07-27.)
  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <section style={cardShell}>
        <SectionHeader />
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Loading…</p>
      </section>
    );
  }
  if (deals.length === 0) {
    return (
      <section style={cardShell}>
        <SectionHeader />
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
          No active deals right now. New under-contract deals will appear here.
        </p>
      </section>
    );
  }

  return (
    <section style={cardShell}>
      <SectionHeader count={deals.length} showCommission={showCommission} />
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {deals.map((d) => (
            <MobileDealRow key={d.id} deal={d} nowSec={nowSec} showCommission={showCommission} />
          ))}
        </div>
      ) : (
      <div style={{
        overflowX: 'auto',
        // Table overflows on narrow viewports — horizontal scroll rather
        // than wrap the columns, so all dates stay on one row per deal.
        marginLeft: -8, marginRight: -8, padding: '0 8px',
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12,
          minWidth: 900,
        }}>
          <thead>
            <tr style={{
              borderBottom: '1px solid var(--border)',
              color: 'var(--text-muted)',
              textAlign: 'left',
              fontSize: 10,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}>
              <th style={hdr}>Deal</th>
              <th style={hdr}>Type</th>
              <th style={hdr}>Contract</th>
              <th style={hdr}>EM Due</th>
              <th style={hdr}>Insp.</th>
              <th style={hdr}>Apprais.</th>
              <th style={hdr}>Fin.</th>
              <th style={hdr}>Walk</th>
              <th style={hdr}>Close</th>
              {showCommission && <th style={{ ...hdr, textAlign: 'right' }}>Commission</th>}
            </tr>
          </thead>
          <tbody>
            {deals.map((d) => (
              <DealRow key={d.id} deal={d} nowSec={nowSec} showCommission={showCommission} />
            ))}
          </tbody>
        </table>
      </div>
      )}
      <div style={{ marginTop: 10, fontSize: 10, color: 'var(--text-muted)' }}>
        <span style={{ color: 'var(--danger, #B92E2F)' }}>■</span> past · still open
        <span style={{ color: '#B36A00', marginLeft: 8 }}>■</span> within 3 days
        <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>—</span> not set
      </div>
    </section>
  );
}

// ---------- helpers ----------

const cardShell = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: '18px 20px',
  marginBottom: 16,
} as const;

const hdr = {
  padding: '6px 8px',
  fontWeight: 500,
  whiteSpace: 'nowrap' as const,
};

const cell = {
  padding: '10px 8px',
  fontVariantNumeric: 'tabular-nums' as const,
  whiteSpace: 'nowrap' as const,
};

function SectionHeader({ count, showCommission }: { count?: number; showCommission?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-heading)', margin: 0 }}>
          Deal deadlines
        </h2>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>
          {count === undefined
            // V-1: the subtitle stops advertising a commission column that is
            // not there for most readers.
            ? (showCommission
              ? 'Every active deal with its key dates + commission.'
              : 'Every active deal with its key dates.')
            : `${count} active deal${count === 1 ? '' : 's'} · buyer, listing, and auction`}
        </p>
      </div>
    </div>
  );
}

/** Type badge label + color, shared by the table row and the mobile card. */
function typeChip(deal: DealDeadline): { label: string; color: string } {
  return {
    label: deal.type === 'buyer' ? 'Buyer' : deal.type === 'auction' ? 'Auction' : 'Listing',
    color: deal.type === 'buyer' ? '#1E6091' : deal.type === 'auction' ? '#8B4513' : '#1F7A3A',
  };
}

/**
 * Commission line:
 *   6% (ours 3%) when we can compute our-side (listing/auction deals)
 *   6% otherwise
 *   — when commission_pct is null
 */
function commissionLabelFor(deal: DealDeadline): string {
  if (deal.commission_pct == null) return '—';
  const pct = (deal.commission_pct * 100).toFixed(2).replace(/\.?0+$/, '');
  if (deal.type !== 'buyer' && deal.buyer_agent_pct != null) {
    const ourPct = Math.max(0, deal.commission_pct - deal.buyer_agent_pct);
    const ourStr = (ourPct * 100).toFixed(2).replace(/\.?0+$/, '');
    return `${pct}% (ours ${ourStr}%)`;
  }
  return `${pct}%`;
}

/** Date color coding shared by the table cell + the mobile date chip. */
function dateTone(
  value: number | null,
  nowSec: number,
  opts: { highlight?: boolean; neverPast?: boolean } = {},
): { color: string; weight: number } {
  if (value == null) return { color: 'var(--text-muted)', weight: 400 };
  const daysDiff = Math.round((value - nowSec) / 86400);
  const isPast = !opts.neverPast && daysDiff < 0;
  const isSoon = !isPast && daysDiff <= 3;
  return {
    color: isPast
      ? 'var(--danger, #B92E2F)'
      : isSoon
      ? '#B36A00'
      : opts.highlight
      ? 'var(--brand-primary, #B92E2F)'
      : 'var(--text-body)',
    weight: isPast || isSoon || opts.highlight ? 500 : 400,
  };
}

/**
 * Phone card variant — the exact same deal + dates, stacked. The date
 * chips sit in an auto-fit grid so they reflow from four-up on a big
 * phone down to two-up on a small one, and never overflow.
 */
function MobileDealRow({
  deal, nowSec, showCommission,
}: { deal: DealDeadline; nowSec: number; showCommission: boolean }) {
  const addressLine = deal.address_street || `Transaction #${deal.id}`;
  const cityLine = [deal.address_city, deal.address_state].filter(Boolean).join(', ');
  const chip = typeChip(deal);
  const dates: Array<{ label: string; value: number | null; highlight?: boolean; neverPast?: boolean }> = [
    { label: 'Contract', value: deal.contract_date, neverPast: true },
    { label: 'Inspection', value: deal.inspection_deadline },
    { label: 'Appraisal', value: deal.appraisal_deadline },
    { label: 'Financing', value: deal.financing_deadline },
    { label: 'Walkthrough', value: deal.walkthrough_date },
    { label: 'Close', value: deal.close_date, highlight: true },
  ];
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '12px 14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Link
            to={`/transactions/${deal.id}`}
            style={{
              color: 'var(--text-heading)',
              fontWeight: 500,
              fontSize: 15,
              textDecoration: 'none',
              display: 'block',
              overflowWrap: 'anywhere',
            }}
          >
            {addressLine}
          </Link>
          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2, overflowWrap: 'anywhere' }}>
            {cityLine || ''}
            {deal.primary_party_names ? <> · {deal.primary_party_names}</> : null}
          </div>
        </div>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 600,
            color: chip.color,
            background: `${chip.color}15`,
            flexShrink: 0,
          }}
        >
          {chip.label}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
          gap: 8,
          marginTop: 10,
        }}
      >
        {dates.map((d) => {
          const tone = dateTone(d.value, nowSec, { highlight: d.highlight, neverPast: d.neverPast });
          return (
            <div key={d.label} style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                  fontWeight: 600,
                }}
              >
                {d.label}
              </div>
              <div style={{ fontSize: 13, color: tone.color, fontWeight: tone.weight, fontVariantNumeric: 'tabular-nums' }}>
                {d.value != null ? formatDate(d.value) : '—'}
              </div>
            </div>
          );
        })}
      </div>
      {/* V-1: the phone card drops the whole commission line, same rule as
          the table column. */}
      {showCommission && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          Commission <strong style={{ color: 'var(--text-heading)', fontWeight: 500 }}>{commissionLabelFor(deal)}</strong>
        </div>
      )}
    </div>
  );
}

function DealRow({
  deal, nowSec, showCommission,
}: { deal: DealDeadline; nowSec: number; showCommission: boolean }) {
  const addressLine = deal.address_street || `Transaction #${deal.id}`;
  const cityLine = [deal.address_city, deal.address_state].filter(Boolean).join(', ');
  const { label: typeLabel, color: typeColor } = typeChip(deal);
  const commissionLabel = commissionLabelFor(deal);

  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={cell}>
        <Link
          to={`/transactions/${deal.id}`}
          style={{
            color: 'var(--text-heading)',
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          {addressLine}
        </Link>
        <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>
          {cityLine || ''}
          {deal.primary_party_names ? (
            <> · {deal.primary_party_names}</>
          ) : null}
        </div>
      </td>
      <td style={cell}>
        <span style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 600,
          color: typeColor,
          background: `${typeColor}15`,
        }}>{typeLabel}</span>
      </td>
      <DateCell value={deal.contract_date} nowSec={nowSec} neverPast />
      <DateCell value={/* auction shows N/A for EM in earnest_money_deadline once we track it —
                           for now, we don't select earnest_money_deadline on this endpoint. */
                        null} nowSec={nowSec} />
      <DateCell value={deal.inspection_deadline} nowSec={nowSec} />
      <DateCell value={deal.appraisal_deadline} nowSec={nowSec} />
      <DateCell value={deal.financing_deadline} nowSec={nowSec} />
      <DateCell value={deal.walkthrough_date} nowSec={nowSec} />
      <DateCell value={deal.close_date} nowSec={nowSec} highlight />
      {showCommission && (
        <td style={{ ...cell, textAlign: 'right', fontWeight: 500 }}>
          {commissionLabel}
        </td>
      )}
    </tr>
  );
}

/** One date column with color coding. `neverPast` skips the red state
 *  for anchor dates (contract effective) which shouldn't nag as past. */
function DateCell({
  value, nowSec, highlight, neverPast,
}: {
  value: number | null;
  nowSec: number;
  highlight?: boolean;
  neverPast?: boolean;
}) {
  if (value == null) {
    return <td style={{ ...cell, color: 'var(--text-muted)' }}>—</td>;
  }
  const { color, weight } = dateTone(value, nowSec, { highlight, neverPast });
  return (
    <td style={{ ...cell, color, fontWeight: weight }}>
      {formatDate(value)}
    </td>
  );
}
